import type { ThreadFilters } from '@/store/thread-filters.store'
import { threadApi } from '@/lib/api/threads.api'
import { useOrganizationStore } from '@/store/organization.store'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  ModuleTypeEnum,
  PaginatedResponse,
  Thread,
  ThreadComment,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'

// Re-export PaginatedResponse for convenience
export type { PaginatedResponse }

// Cache time constants for threads
// Realtime updates handle new data, so we can use longer stale times to reduce API calls
const THREADS_STALE_TIME = 60 * 1000 // 1 minute - thread list data considered fresh
const THREADS_GC_TIME = 10 * 60 * 1000 // 10 minutes - keep in cache
const THREAD_COMMENTS_STALE_TIME = 2 * 60 * 1000 // 2 minutes - comments fetched on thread selection, realtime handles new ones

/**
 * Hook to fetch threads with filters
 * Uses optimized caching for faster perceived load times
 */
export function useThreadsQuery(
  filters?: ThreadFilters,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  const query = useQuery<PaginatedResponse<Thread>>({
    queryKey: queryKeys.threads.list(filters),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const params: Record<string, string | number> = {
        page: filters?.page || 1,
        limit: filters?.limit || 50,
      }

      if (filters?.title) {
        params.title = filters.title
      }

      if (filters?.sortBy) {
        params.sortBy = filters.sortBy
      }

      if (filters?.sortOrder) {
        params.sortOrder = filters.sortOrder
      }

      if (filters?.moduleType) {
        params.moduleType = filters.moduleType
      }

      if (filters?.moduleId) {
        params.moduleId = filters.moduleId
      }

      if (filters?.threadType) {
        params.threadType = filters.threadType
      }

      if (filters?.membershipOnly !== undefined) {
        (params as Record<string, unknown>).membershipOnly =
          filters.membershipOnly
      }

      if (filters?.excludeComments !== undefined) {
        (params as Record<string, unknown>).excludeComments =
          filters.excludeComments
      }

      return threadApi.getThreads(
        params as Record<string, string | number | undefined>,
      )
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    // Caching configuration
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    // Use previous data while fetching new data for smoother transitions
    // BUT components should check isPlaceholderData to avoid showing wrong tab's data
    placeholderData: previousData => previousData,
    // Refetch on window focus only if data is stale
    refetchOnWindowFocus: true,
    // Don't refetch on mount if data is fresh
    refetchOnMount: true,
  })

  return {
    ...query,
    // isPlaceholderData: true when showing cached data from a different query while fetching
    // Components should show loading state when this is true to avoid showing wrong data
    isPlaceholderData: query.isPlaceholderData,
  }
}

const THREADS_PAGE_SIZE = 30
type ThreadQueryFilters = Pick<
  ThreadFilters,
  | 'title'
  | 'sortBy'
  | 'sortOrder'
  | 'moduleType'
  | 'moduleId'
  | 'threadType'
  | 'membershipOnly'
  | 'excludeComments'
>

/**
 * Hook to fetch threads with infinite scroll pagination
 */
export function useInfiniteThreadsQuery(
  filters?: ThreadQueryFilters,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  // Create a stable, serializable query key that clearly differentiates tabs
  // Extract key filter values explicitly to ensure proper cache separation
  const queryKey = [
    ...queryKeys.threads.all,
    'infinite',
    {
      threadType: filters?.threadType ?? 'all',
      membershipOnly: filters?.membershipOnly,
      excludeComments: filters?.excludeComments,
      moduleType: filters?.moduleType,
      moduleId: filters?.moduleId,
      title: filters?.title,
      sortBy: filters?.sortBy,
      sortOrder: filters?.sortOrder,
    },
  ] as const

  const query = useInfiniteQuery<PaginatedResponse<Thread>>({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const params: Record<string, string | number> = {
        page: pageParam as number,
        limit: THREADS_PAGE_SIZE,
      }

      if (filters?.title) {
        params.title = filters.title
      }

      if (filters?.sortBy) {
        params.sortBy = filters.sortBy
      }

      if (filters?.sortOrder) {
        params.sortOrder = filters.sortOrder
      }

      if (filters?.moduleType) {
        params.moduleType = filters.moduleType
      }

      if (filters?.moduleId) {
        params.moduleId = filters.moduleId
      }

      if (filters?.threadType) {
        params.threadType = filters.threadType
      }

      if (filters?.membershipOnly !== undefined) {
        (params as Record<string, unknown>).membershipOnly =
          filters.membershipOnly
      }

      if (filters?.excludeComments !== undefined) {
        (params as Record<string, unknown>).excludeComments =
          filters.excludeComments
      }

      return threadApi.getThreads(
        params as Record<string, string | number | undefined>,
      )
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.meta?.hasNextPage) {
        return lastPage.meta.currentPage + 1
      }
      return undefined
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
  })

  // Flatten all pages into a single array
  const threads = query.data?.pages.flatMap(page => page.data) ?? []

  // isFetching is true during initial load AND when fetching for a new query key
  // This helps detect tab switches where we need to show loading state
  const isFetching = query.isFetching

  return {
    ...query,
    threads,
    isFetching,
  }
}

/**
 * Prefetch threads for a specific tab (e.g., when hovering over tab)
 */
export function usePrefetchThreads() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganizationStore()

  return async (filters: ThreadFilters) => {
    if (!currentOrganization?.id) return

    const params: Record<string, string | number> = {
      page: filters?.page || 1,
      limit: filters?.limit || 50,
    }

    if (filters?.sortBy) params.sortBy = filters.sortBy
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder
    if (filters?.threadType) params.threadType = filters.threadType

    await queryClient.prefetchQuery({
      queryKey: queryKeys.threads.list(filters),
      queryFn: () =>
        threadApi.getThreads(
          params as Record<string, string | number | undefined>,
        ),
      staleTime: THREADS_STALE_TIME,
    })
  }
}

/**
 * Hook to fetch a single thread by ID
 */
export function useThreadQuery(
  id: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<Thread>({
    queryKey: queryKeys.threads.detail(id!),
    queryFn: async () => {
      if (!id) {
        throw new Error('Thread ID is required')
      }
      return threadApi.getThread(id)
    },
    enabled:
      (options?.enabled !== false && !!id && !!currentOrganization?.id) ??
      false,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

/**
 * Hook to fetch threads for a specific module
 */
export function useThreadsByModuleQuery(
  moduleType: ModuleTypeEnum | undefined,
  moduleId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<Thread[]>({
    queryKey: [
      ...queryKeys.threads.all,
      'module',
      moduleType,
      moduleId,
    ] as const,
    queryFn: async () => {
      if (!moduleType || !moduleId) {
        return []
      }
      const response = await threadApi.getThreads({
        moduleType,
        moduleId,
        limit: 50, // Reasonable limit - implement pagination if more needed
      })
      return response.data
    },
    enabled:
      (options?.enabled !== false &&
        !!moduleType &&
        !!moduleId &&
        !!currentOrganization?.id) ??
      false,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
  })
}

/**
 * Hook to get the count of unread threads.
 * Uses efficient dedicated endpoint that counts in the database.
 * Uses a longer stale time to avoid excessive API calls.
 * @param threadType - Optional filter to count only specific thread types (e.g., DIRECT_MESSAGE, DISCUSSION)
 */
export function useUnreadThreadsCount(options?: {
  enabled?: boolean
  threadType?: string
}) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<number>({
    queryKey: queryKeys.threads.unreadCount(options?.threadType),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        return 0
      }
      // Use efficient endpoint that counts in the database
      const response = await threadApi.getUnreadCount(options?.threadType)
      return response.count
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - avoid excessive API calls for badge
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false, // Don't refetch on every tab switch - use realtime updates
  })
}

/**
 * Hook to fetch thread comments
 *
 * For DISCUSSION threads: fetches newest comments first (desc) so the most recent
 * messages are always available. The UI then reverses them for chronological display.
 * This ensures users see the latest conversation when opening a thread.
 */
export function useThreadCommentsQuery(
  threadId: string | undefined,
  options?: {
    parentCommentId?: string | null
    limit?: number
    page?: number
    enabled?: boolean
    sortOrder?: 'asc' | 'desc'
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<PaginatedResponse<ThreadComment>>({
    queryKey: [
      ...queryKeys.threads.detail(threadId!),
      'comments',
      options?.parentCommentId,
      options?.page,
      options?.limit,
      options?.sortOrder,
    ] as const,
    queryFn: async () => {
      if (!threadId) {
        throw new Error('Thread ID is required')
      }
      return threadApi.getThreadComments({
        threadId,
        parentCommentId: options?.parentCommentId,
        limit: options?.limit,
        page: options?.page,
        sortOrder: options?.sortOrder,
      })
    },
    enabled:
      (options?.enabled !== false && !!threadId && !!currentOrganization?.id) ??
      false,
    staleTime: THREAD_COMMENTS_STALE_TIME, // Longer stale time - realtime handles new comments
    gcTime: THREADS_GC_TIME,
    placeholderData: previousData => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}
